import { BasisProvider } from "basis";
import { AccountLinkPage } from "components/AccountLink/AccountLinkPage";
import { AccountsDashboard } from "components/AccountsDashboard/AccountsDashboard";
import { ActivateCard } from "components/ActivateCard/ActivateCard";
import { TermsAndConditions } from "components/Auth/TermsAndConditions";
import { Bpay } from "components/Bpay/Bpay";
import { CloseAccount } from "components/CloseAccount/CloseAccount";
import { Contact } from "components/Contact/Contact";
import { CreditCardAccount } from "components/CreditCardAccount/CreditCardAccount";
import { DebitCardPaymentPage } from "components/DebitCardPaymentPage/DebitCardPaymentPage";
import { DirectDebit } from "components/DirectDebit/DirectDebit";
import { Faqs } from "components/Faqs/Faqs";
import { HomePage } from "components/HomePage/HomePage";
import { LogoutPage } from "components/Logout/Logout";
import { OverseasTravel } from "components/OverseasTravel";
import { PersonalLoanAccount } from "components/PersonalLoanAccount/PersonalLoanAccount";
import { ReduceCreditLimit } from "components/ReduceCreditLimit/ReduceCreditLimit";
import { RegistrationFlow as Registration } from "components/Registration/RegistrationFlow";
import { RequestLetter } from "components/RequestLetter/RequestLetter";
import { AuthRoute } from "components/Route/AuthRoute";
import { PublicRoute } from "components/Route/PublicRoute";
import { RouterScrollTop } from "components/RouterScrollTop/RouterScrollTop";
import { SetPin } from "components/SetPin/SetPin";
import { StatementPreference } from "components/StatementPreference";
import { TransactionDispute } from "components/TransactionDispute";
import { DebitCardSuccessPage } from "components/WaysToPay/DebitCardSuccessPage/DebitCardSuccessPage";
import { ErrorPage } from "components/WaysToPay/Error/ErrorPage";
import { PayIn } from "components/WaysToPay/PayIn/PayIn";
import { WaysToPay } from "components/WaysToPay/WaysToPay";
import "core-js/stable";
import { getIsProduction } from "library/browser";
import { initDataDog } from "library/datadog";
import { getRegional } from "library/getRegional";
import { UserProvider } from "library/hooks/auth";
import { useConfigFeature, usePrefetchConfig } from "library/hooks/useConfig";
import { useTealium } from "library/hooks/useTealium";
import threatmetrix from "library/threatmetrix";
import { HardshipApplication } from "pages/HardshipApplication/HardshipApplication";
import { IncreaseCreditLimitApplication } from "pages/IncreaseCreditLimit/IncreaseCreditLimitApplication";
import { LostStolenOrDamagedPage } from "pages/LostStolenOrDamaged";
import { FC, useEffect } from "react";
import ReactDOM from "react-dom";
import { useSiteTracking } from "react-event-tracker";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { BrowserRouter, Link, Route, Routes } from "react-router-dom";
import "regenerator-runtime/runtime";
import { v4 as uuid } from "uuid";
import { theme } from "./basisTheme";
import { AdditionalCardHolder } from "./pages/AdditionalCardHolder";
import { ManageAccount } from "./pages/ManageAccount/ManageAccount";
import { ManagePersonalDetails } from "./pages/ManagePersonalDetails/ManagePersonalDetails";
import { UpdateHomeWorkNumberPage } from "./pages/ManagePersonalDetails/UpdateHomeWorkNumber/index";
import { UpdateMobileNumberPage } from "./pages/ManagePersonalDetails/UpdateMobileNumber";
import { NotificationsPage } from "./pages/Notifications";
import { PasswordRecovery } from "./pages/PasswordRecovery/PasswordRecovery";
import { TemporarilyLockCardPage } from "./pages/TemporaryLockUnlockCard";
import { ThirdPartyAuthority } from "./pages/ThirdPartyAuthority/ThirdPartyAuthority";
import { trackingConfig } from "./tealiumConfig";

// Initialize the data dog client immediately.
initDataDog();

const Pages: FC = () => {
  const isNZ = getRegional("code") === "NZ";
  const isDirectDebitAvailable = useConfigFeature("directDebit") && !isNZ;
  const isBpayAvailable = useConfigFeature("bpay") && !isNZ;
  const isIncreaseCreditLimitAUAvailable = useConfigFeature(
    "increaseCreditLimitAU",
  );
  const isIncreaseCreditLimitNZAvailable = useConfigFeature(
    "increaseCreditLimitNZ",
  );
  const isRequestLetterAvailable = useConfigFeature("requestLetter");
  const isAdditionalCardHolderAvailable = useConfigFeature(
    "additionalCardHolder",
  );
  const isThirdPartyAuthorityAvailable = useConfigFeature(
    "thirdPartyAuthority",
  );
  const isHardshipAvailable = useConfigFeature("hardship");

  const isMobileDebitCardPaymentAvailable = useConfigFeature(
    "mobileDebitCardPayment",
  );

  const isStatementPreferencesAvailable = useConfigFeature(
    "showStatementPreferences",
  );

  const isUpdateHomeWorkPhones = useConfigFeature("updateHomeWorkPhones");
  const isTempCardLockUnlock = useConfigFeature("tempCardLockUnlock");
  const showLostStolenOrDamaged = useConfigFeature("lostStolenOrDamaged");

  usePrefetchConfig();

  return (
    <BrowserRouter>
      <RouterScrollTop>
        <Routes>
          <AuthRoute path="/" exact component={AccountsDashboard} />

          <AuthRoute path="/activate-card" component={ActivateCard} />
          {isBpayAvailable && <AuthRoute path="/bpay" component={Bpay} />}

          <AuthRoute
            path="/credit-card-account"
            component={CreditCardAccount}
          />

          <AuthRoute
            path="/personal-loan-account"
            component={PersonalLoanAccount}
            noStateRedirect="/"
          />

          <AuthRoute path="/link-account" component={AccountLinkPage} />
          <AuthRoute path="/overseas-travel" component={OverseasTravel} />

          <AuthRoute path="/ways-to-pay" component={WaysToPay} />
          <AuthRoute
            path="/ways-to-pay/debit-card/success"
            component={DebitCardSuccessPage}
          />
          <AuthRoute path="/ways-to-pay/error" component={ErrorPage} />
          {isDirectDebitAvailable && (
            <AuthRoute path="/direct-debit" component={DirectDebit} />
          )}

          <AuthRoute
            path="/reduce-credit-limit"
            component={ReduceCreditLimit}
          />

          {isAdditionalCardHolderAvailable && (
            <AuthRoute
              path="/additional-card-holder"
              component={AdditionalCardHolder}
            />
          )}

          {isThirdPartyAuthorityAvailable && (
            <AuthRoute
              path="/third-party-authority"
              component={ThirdPartyAuthority}
            />
          )}

          {(isIncreaseCreditLimitAUAvailable ||
            isIncreaseCreditLimitNZAvailable) && (
            <Route
              path="/increase-credit-limit/*"
              element={<IncreaseCreditLimitApplication />}
            />
          )}
          {isRequestLetterAvailable && (
            <AuthRoute
              path="/request-letter"
              component={RequestLetter}
              noStateRedirect="/manage-account"
            />
          )}
          <AuthRoute
            path="/close-account"
            component={CloseAccount}
            noStateRedirect="/manage-account"
          />
          {isStatementPreferencesAvailable && (
            <AuthRoute
              path="/statement-preferences"
              component={StatementPreference}
              noStateRedirect="/manage-account"
            />
          )}

          <AuthRoute path="/manage-account" component={ManageAccount} />

          <AuthRoute path="/pay-in" component={PayIn} />

          <AuthRoute path="/contact" component={Contact} />

          <AuthRoute path="/set-pin" component={SetPin} />

          <AuthRoute
            path="/manage-personal-details/update-mobile-number/*"
            component={UpdateMobileNumberPage}
          />

          <AuthRoute
            path="/manage-personal-details/update-home-number/*"
            component={UpdateHomeWorkNumberPage}
            featureFlag={isUpdateHomeWorkPhones}
          />

          <AuthRoute
            path="/manage-personal-details/update-work-number/*"
            component={UpdateHomeWorkNumberPage}
            featureFlag={isUpdateHomeWorkPhones}
          />

          <AuthRoute
            path="/manage-personal-details/*"
            component={ManagePersonalDetails}
          />

          <AuthRoute path="/notifications" component={NotificationsPage} />

          <AuthRoute
            path="/transaction-dispute"
            component={TransactionDispute}
            noStateRedirect="/manage-account"
          />

          <AuthRoute
            path="/temporarily-lock-card"
            component={TemporarilyLockCardPage}
            noStateRedirect="/manage-account"
            featureFlag={isTempCardLockUnlock}
          />

          <AuthRoute
            path="/lost-stolen-damaged"
            component={LostStolenOrDamagedPage}
            noStateRedirect="/manage-account"
            featureFlag={showLostStolenOrDamaged}
          />

          <PublicRoute
            path="/register"
            hasLandingWrapper
            component={Registration}
          />
          <PublicRoute
            path="/forgot-password"
            hasLandingWrapper
            component={PasswordRecovery}
          />

          <PublicRoute path="/login" component={HomePage} />

          {isHardshipAvailable && (
            <PublicRoute path="/hardship/*" component={HardshipApplication} />
          )}

          {isMobileDebitCardPaymentAvailable && (
            <Route
              path="/payment/debit-card"
              element={<DebitCardPaymentPage />}
            />
          )}

          <PublicRoute
            path="/terms"
            hasLandingWrapper
            component={TermsAndConditions}
          />

          <PublicRoute path="/logout" component={LogoutPage} />

          <PublicRoute path="/faq" component={Faqs} />
        </Routes>
      </RouterScrollTop>
    </BrowserRouter>
  );
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const App: FC = () => {
  useEffect(() => {
    const sessionId = uuid();

    window.sessionStorage.setItem("sessionId", sessionId);

    /* cspell:disable-next-line */
    const threatMetrixOrgID = getIsProduction() ? "7rflktri" : "8t5e2xlp";

    threatmetrix.profile(
      "id.latitudefinancial.com",
      threatMetrixOrgID,
      sessionId,
    );
  }, []);

  useTealium();

  const { SiteTracking } = useSiteTracking(trackingConfig);

  return (
    <QueryClientProvider client={queryClient}>
      <BasisProvider theme={theme} InternalLink={Link} isRoot>
        <SiteTracking>
          <UserProvider>
            <Pages />
          </UserProvider>
        </SiteTracking>
      </BasisProvider>
      {process.env.NODE_ENV !== "test" && window.Cypress === undefined && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
};

const wrapper = document.getElementById("app");
if (wrapper) ReactDOM.render(<App />, wrapper);
